import {Injectable, Optional} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {
  appleAuthScript,
  FelloApiService,
  FelloAuthAPIService,
  LoadScriptService,
  mixinDestroyable,
  MixPanelEventTracker,
  StateTransferHelperService
} from "fello-common";
import {FellOfferJourneyPushDataResponse, FelloVisitorVariables, GoogleUser} from "fello-model";
import {combineLatest, NEVER, Observable, of, ReplaySubject, throwError} from "rxjs";
import {catchError, distinctUntilChanged, map, switchMap, take, takeUntil, tap} from "rxjs/operators";
import {cloneDeep, forEach} from "lodash-es";
import {OfferJourneyStatusMapping, QuestionStatus} from "../../mapping/OfferJourneyStatusMapping";
import {HttpErrorResponse, HttpStatusCode} from "@angular/common/http";
import moment from "moment";

@Injectable()
export class OfferJourneyService extends mixinDestroyable(class {}) {
  private felloOfferJourneySubject = new ReplaySubject<FellOfferJourneyPushDataResponse>(1);
  felloOfferJourney = this.felloOfferJourneySubject.asObservable();

  private offerJourneyStatusMappingSubject = new ReplaySubject<typeof OfferJourneyStatusMapping>(1);
  offerJourneyStatusMapping = this.offerJourneyStatusMappingSubject.asObservable();

  journeyId$ = this.activatedRoute.params.pipe(map(data => data.journeyId));

  constructor(
    private felloApiService: FelloApiService,
    public activatedRoute: ActivatedRoute,
    public router: Router,
    private stateTransferHelperService: StateTransferHelperService,
    private loadScriptService: LoadScriptService,
    private felloAuthApiService: FelloAuthAPIService,
    @Optional() private mixPanelEventTracker?: MixPanelEventTracker
  ) {
    super();
    this.offerJourneyStatusMappingSubject.next(cloneDeep(OfferJourneyStatusMapping));
    this.journeyId$
      .pipe(
        takeUntil(this.isDestroyed),
        tap(dashboardId => {
          this.mixPanelEventTracker?.setUserId(dashboardId);
          this.mixPanelEventTracker?.updateUserData({
            dashboardId,
            lastActivityTime: moment().format()
          });
        }),
        switchMap(dashboardId =>
          this.getOfferJourney().pipe(
            tap(({journeyType, next, origin}) => {
              this.mixPanelEventTracker?.trackCustomEvent("landed_on_journey", {
                taskId: next,
                journeyType,
                origin,
                dashboardId
              });
            }),
            catchError(err => {
              if (err instanceof HttpErrorResponse) {
                if (err.status === HttpStatusCode.NotFound) {
                  this.router.navigate(["/not-found"], {
                    skipLocationChange: true
                  });
                  return NEVER;
                }
              }
              return throwError(err);
            })
          )
        )
      )
      .subscribe();
    combineLatest([this.journeyId$.pipe(distinctUntilChanged()), this.felloOfferJourneySubject])
      .pipe(takeUntil(this.isDestroyed))
      .subscribe(([dashboardId, {next, journeyType, origin}]) => {
        this.mixPanelEventTracker?.trackCustomEvent(next, {
          journeyType,
          origin,
          dashboardId
        });
      });
  }

  getOfferJourney(): Observable<FellOfferJourneyPushDataResponse> {
    return this.journeyId$.pipe(
      take(1),
      switchMap(dashboardId =>
        this.stateTransferHelperService.transferOnce(this.felloApiService.locateOfferJourney(dashboardId), `d:${dashboardId}`)
      ),
      tap(offerJourney => {
        this.updateOfferJourney(offerJourney);
      }),
      catchError((err: HttpErrorResponse) => {
        if (err) {
          this.router.navigate(["/not-found"]);
        }
        return throwError(err);
      })
    );
  }

  markTaskAsLoading(taskId: string): void {
    this.offerJourneyStatusMapping
      .pipe(
        take(1),
        tap(mapping => {
          const offerJourneyStatusMapping = mapping;
          offerJourneyStatusMapping[taskId] = QuestionStatus.LOADING;
          this.offerJourneyStatusMappingSubject.next(offerJourneyStatusMapping);
        })
      )
      .subscribe();
  }

  pushOfferJourneyTask(taskId: string, vars: FelloVisitorVariables): Observable<unknown> {
    this.markTaskAsLoading(taskId);

    return this.journeyId$.pipe(
      take(1),
      switchMap(dashboardId => this.pushOfferJourneyTaskDirect(dashboardId, taskId, vars)),
      tap(offerJourney => {
        this.updateOfferJourney(offerJourney);
      }),
      catchError((err: HttpErrorResponse) => {
        if (err) {
          this.offerJourneyStatusMapping
            .pipe(
              take(1),
              tap(mapping => {
                const offerJourneyStatusMapping = mapping;
                offerJourneyStatusMapping[taskId] = QuestionStatus.ACTIVE;
                this.offerJourneyStatusMappingSubject.next(offerJourneyStatusMapping);
              })
            )
            .subscribe();
          return of(null);
        }
        return throwError(err);
      })
    );
  }

  pushOfferJourneyTaskDirect(
    dashboardId: string,
    taskId: string,
    vars: FelloVisitorVariables
  ): Observable<FellOfferJourneyPushDataResponse> {
    return this.felloApiService.pushOfferJourneyTask(dashboardId, taskId, vars).pipe(
      tap(({journeyType, next, origin, vars}) => {
        this.mixPanelEventTracker?.updateUserData({
          lastCompletedTask: taskId,
          next,
          journeyType,
          origin,
          dashboardId,
          lastActivityTime: moment().format(),
          address: vars.address,
          serviceable: vars._serv,
          ioServiceable: vars._ioServ
        });
      })
    );
  }

  private updateOfferJourney(offerJourney: FellOfferJourneyPushDataResponse): void {
    this.offerJourneyStatusMapping
      .pipe(
        take(1),
        tap(mapping => {
          const offerJourneyStatusMapping = mapping;
          forEach(offerJourney.userTrail, task => {
            offerJourneyStatusMapping[task] = QuestionStatus.COMPLETE;
          });
          offerJourneyStatusMapping[offerJourney.next] = QuestionStatus.ACTIVE;
          this.offerJourneyStatusMappingSubject.next(offerJourneyStatusMapping);
          this.felloOfferJourneySubject.next(offerJourney);
        })
      )
      .subscribe();
  }

  updateStatus(taskId: string, status: QuestionStatus): void {
    this.offerJourneyStatusMapping
      .pipe(
        take(1),
        tap(mapping => {
          const offerJourneyStatusMapping = mapping;
          offerJourneyStatusMapping[taskId] = status;
          this.offerJourneyStatusMappingSubject.next(offerJourneyStatusMapping);
        })
      )
      .subscribe();
  }

  loginWithGoogle(): Observable<GoogleUser | null> {
    return this.felloAuthApiService.loginWithGoogle();
  }
  loadAppleAuthenticationScript(): Observable<unknown> {
    return this.loadScriptService.load(appleAuthScript);
  }
}
